import React, {useState} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import messages from '../../../../res/translations/messages';
import ConditionButton from './ConditionButton';
import {Modal, ModalHeader} from 'reactstrap';
import {Formik, yupToFormErrors} from 'formik';
import ScriptGeneratorClassifier2ChoicesFormContainer from './ScriptGeneratorClassifier2ChoicesFormContainer';
import {get, set, cloneDeep, map, flatten} from 'lodash';
import {getChoiceScriptValidatorSchema} from '../../../services/logicValidatorSchemas';
import Yup from '../../../yup';

const getValidationSchema = (pageId) => (
    Yup.object().shape({
        choiceScripts: Yup.array().of(getChoiceScriptValidatorSchema(pageId)),
    })
);

const getValidate = (context, id, {isLastChoice = false} = {}) => {
    if (!context) {
        return () => ({});
    }

    return async (values) => {
        if (!context.pageId) {
            return {};
        }

        const scripts = get(values, id);
        try {
            await getValidationSchema(context.pageId).validate(
                {choiceScripts: scripts},
                {abortEarly: false, context: {pageItems: context.pageItems, pages: context.pages}},
            );
        } catch (err) {
            throw yupToFormErrors(err);
        }

        if (!isLastChoice && !hasAtLeastOneCondition(scripts)) {
            return {[id]: {key: 'unreachableChoice'}};
        }

        return {};
    };
};

const hasAtLeastOneCondition = (scripts) => (
    Array.isArray(scripts) &&
    scripts.some((script) => Array.isArray(script.expression) && script.expression.length > 0)
);

const buildModalContext = (formValues, pageIndex) => {
    const editorPages = cloneDeep(get(formValues, 'editor.pages', []));
    const items = flatten(map(editorPages, (page) => page.items || []));
    const pageItems = flatten(
        map(editorPages,
            (page, index) =>
                map(page.items || [], (item) => ({
                    pageIndex: index,
                    pageId: page.id,
                    ...item,
                }))))
        ;

    const pages = map(editorPages, (page) => ({
        id: page.id,
        choices: map(flatten(map(page.items || [], (item) => item.choices || [])), 'id'),
        rows: map(flatten(map(page.items || [], (item) => item.rows || [])), 'id'),
    }));
    const extraFields = get(formValues, 'extraFields', []);
    const parameters = get(formValues, 'parameters', []);
    return {
        editorPages,
        items,
        pageItems,
        pages,
        pageId: get(editorPages, `[${pageIndex}].id`),
        extraFields,
        parameters,
    };
};

// TODO remove disable lint
/* eslint-disable react/jsx-no-bind */
export const ScriptGeneratorClassifier2ChoicesButton = ({form, id, currPage, isLastChoice = false}) => {
    const [modal, setModal] = useState(false);
    const [subFormInitialValues, setSubFormInitialValues] = useState(null);
    const [modalContext, setModalContext] = useState(null);

    const closeModal = () => {
        setModal(false);
        setSubFormInitialValues(null);
        setModalContext(null);
    };

    const openModal = () => {
        const scripts = cloneDeep(get(form.values, id)) || [];
        setSubFormInitialValues({choiceScripts: scripts});
        setModalContext(buildModalContext(form.values, currPage));
        setModal(true);
    };

    const cancel = () => closeModal();

    const handleModalClick = (e) => {
        e.stopPropagation();
    };

    const ensureTouchedContainer = () => {
        const currentTouched = form.touched || {};
        const fieldTouched = get(currentTouched, id);
        if (fieldTouched && typeof fieldTouched === 'object') {
            return;
        }

        const nextTouched = cloneDeep(currentTouched);
        set(nextTouched, id, {});
        form.setTouched(nextTouched, false);
    };

    const handleConfirm = async (values, actions) => {
        const updatedScripts = cloneDeep(get(values, 'choiceScripts', []));
        const nextValues = cloneDeep(form.values);
        set(nextValues, id, updatedScripts);

        form.setFieldValue(id, updatedScripts, false);
        ensureTouchedContainer();
        await form.validateForm(nextValues);
        actions.setSubmitting(false);
        closeModal();
    };

    const previousPages = modalContext ? modalContext.editorPages.slice(0, currPage) : [];
    const items = modalContext ? modalContext.items : [];
    const extraFields = modalContext ? modalContext.extraFields : [];
    const parameters = modalContext ? modalContext.parameters : [];

    return (
        <ConditionButton onClick={openModal}>
            <Modal isOpen={modal} toggle={cancel} backdrop="static">
                <ModalHeader toggle={cancel}>
                    <FormattedMessage {...messages.surveys.modals.scriptGeneratorClassifier2.title} />
                </ModalHeader>
                {modal && subFormInitialValues && modalContext &&
                    <Formik
                        enableReinitialize
                        initialValues={subFormInitialValues}
                        onSubmit={handleConfirm}
                        validate={getValidate(modalContext, 'choiceScripts', {isLastChoice})}
                        validateOnChange={true}
                        validateOnBlur={false}
                    >
                        {(modalForm) => (
                            <ScriptGeneratorClassifier2ChoicesFormContainer
                                formikBag={modalForm}
                                cancel={cancel}
                                handleModalClick={handleModalClick}
                                previousPages={previousPages}
                                items={items}
                                extraFields={extraFields}
                                parameters={parameters}
                            />
                        )}
                    </Formik>
                }
            </Modal>
        </ConditionButton>);
};

export default injectIntl(ScriptGeneratorClassifier2ChoicesButton);
